"use client";

import { useState } from "react";
import { SelectField, TextField } from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  CheckResults,
  type CheckStrings,
} from "@/components/tools/chem-reference/solubility-check";
import {
  KspResults,
  type KspStrings,
  KspTable,
} from "@/components/tools/chem-reference/solubility-ksp";
import {
  MixResults,
  type MixStrings,
} from "@/components/tools/chem-reference/solubility-mix";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

export interface PanelFieldStrings {
  results: string;
  formulaLabel: string;
  formulaHint: string;
  formulaPlaceholder: string;
  tryLabel: string;
  firstLabel: string;
  secondLabel: string;
  concLabel: string;
  volumeLabel: string;
  mixHint: string;
  kspLabel: string;
  kspHint: string;
  cationInitLabel: string;
  anionInitLabel: string;
  commonHint: string;
  digitsLabel: string;
}

export type PanelStrings = CheckStrings &
  MixStrings &
  KspStrings &
  PanelFieldStrings;

const CHECK_EXAMPLES = [
  "BaSO4",
  "NaCl",
  "AgCl",
  "Pb(NO3)2",
  "CaCO3",
  "Ca(OH)2",
  "Na2S",
  "Al(OH)3",
  "(NH4)3PO4",
  "PbCl2",
] as const;

const MIX_EXAMPLES: readonly (readonly [string, string])[] = [
  ["Pb(NO3)2", "KI"],
  ["AgNO3", "NaCl"],
  ["BaCl2", "Na2SO4"],
  ["CaCl2", "Na3PO4"],
  ["CuSO4", "NaOH"],
  ["NaCl", "KNO3"],
];

const KSP_EXAMPLES = [
  "AgCl",
  "Ag2CrO4",
  "CaF2",
  "PbI2",
  "Ca3(PO4)2",
  "Fe(OH)3",
] as const;

const DIGITS = ["2", "3", "4"] as const;

function Examples<T extends string>({
  label,
  items,
  text,
  onPick,
}: {
  label: string;
  items: readonly T[];
  text: (item: T) => string;
  onPick: (item: T) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className={LABEL}>{label}</span>
      {items.map((item) => (
        <button
          className={BUTTON_SMALL}
          key={item}
          onClick={() => onPick(item)}
          type="button"
        >
          {text(item)}
        </button>
      ))}
    </div>
  );
}

/** Blank → null; unreadable → NaN. */
function read(raw: string): number | null {
  if (raw.trim() === "") {
    return null;
  }
  return parseMeasuredNumber(raw) ?? Number.NaN;
}

export function CheckPanel({ strings }: { strings: PanelStrings }) {
  const fmt = useToolNumber();
  const [formula, setFormula] = useState("BaSO4");
  return (
    <>
      <TextField
        hint={strings.formulaHint}
        id="sr-formula"
        label={strings.formulaLabel}
        mono
        onChange={setFormula}
        placeholder={strings.formulaPlaceholder}
        value={formula}
      />
      <Examples
        items={CHECK_EXAMPLES}
        label={strings.tryLabel}
        onPick={setFormula}
        text={(item) => item}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <CheckResults fmt={fmt} formula={formula} strings={strings} />
      </section>
    </>
  );
}

export function MixPanel({ strings }: { strings: PanelStrings }) {
  const fmt = useToolNumber();
  const [first, setFirst] = useState("Pb(NO3)2");
  const [second, setSecond] = useState("KI");
  const [c1, setC1] = useState("0.10");
  const [v1, setV1] = useState("50");
  const [c2, setC2] = useState("0.10");
  const [v2, setV2] = useState("50");
  const inputs = {
    first,
    second,
    c1: read(c1),
    v1: read(v1),
    c2: read(c2),
    v2: read(v2),
  };
  const numbers = [inputs.c1, inputs.v1, inputs.c2, inputs.v2].map((v) =>
    v !== null && v > 0 ? v : null
  );
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.formulaHint}
          id="sr-first"
          label={strings.firstLabel}
          mono
          onChange={setFirst}
          value={first}
        />
        <TextField
          id="sr-second"
          label={strings.secondLabel}
          mono
          onChange={setSecond}
          value={second}
        />
      </div>
      <Examples
        items={MIX_EXAMPLES.map(([a, b]) => `${a}|${b}`)}
        label={strings.tryLabel}
        onPick={(item) => {
          const [a, b] = item.split("|");
          setFirst(a);
          setSecond(b);
        }}
        text={(item) => item.replace("|", " + ")}
      />
      <p className="text-muted-foreground text-xs">{strings.mixHint}</p>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <TextField
          id="sr-c1"
          label={t(strings.concLabel, { n: "1" })}
          numeric
          onChange={setC1}
          value={c1}
        />
        <TextField
          id="sr-v1"
          label={t(strings.volumeLabel, { n: "1" })}
          numeric
          onChange={setV1}
          value={v1}
        />
        <TextField
          id="sr-c2"
          label={t(strings.concLabel, { n: "2" })}
          numeric
          onChange={setC2}
          value={c2}
        />
        <TextField
          id="sr-v2"
          label={t(strings.volumeLabel, { n: "2" })}
          numeric
          onChange={setV2}
          value={v2}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <MixResults
          fmt={fmt}
          inputs={{
            first,
            second,
            c1: numbers[0],
            v1: numbers[1],
            c2: numbers[2],
            v2: numbers[3],
          }}
          strings={strings}
        />
      </section>
    </>
  );
}

export function KspPanel({ strings }: { strings: PanelStrings }) {
  const fmt = useToolNumber();
  const [formula, setFormula] = useState("AgCl");
  const [ksp, setKsp] = useState("");
  const [cation, setCation] = useState("");
  const [anion, setAnion] = useState("0.10");
  const [digits, setDigits] = useState<(typeof DIGITS)[number]>("2");
  const kspValue = read(ksp);
  const cationValue = read(cation);
  const anionValue = read(anion);
  const bad = (v: number | null) => v !== null && Number.isNaN(v);
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.formulaHint}
          id="sr-ksp-formula"
          label={strings.formulaLabel}
          mono
          onChange={setFormula}
          placeholder={strings.formulaPlaceholder}
          value={formula}
        />
        <TextField
          hint={strings.kspHint}
          id="sr-ksp"
          label={strings.kspLabel}
          numeric
          onChange={setKsp}
          placeholder="1.8e-10"
          value={ksp}
        />
      </div>
      <Examples
        items={KSP_EXAMPLES}
        label={strings.tryLabel}
        onPick={(item) => {
          setFormula(item);
          setKsp("");
        }}
        text={(item) => item}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          hint={strings.commonHint}
          id="sr-cation-init"
          label={strings.cationInitLabel}
          numeric
          onChange={setCation}
          value={cation}
        />
        <TextField
          id="sr-anion-init"
          label={strings.anionInitLabel}
          numeric
          onChange={setAnion}
          value={anion}
        />
        <SelectField
          id="sr-digits"
          label={strings.digitsLabel}
          onChange={setDigits}
          options={DIGITS.map((value) => ({ value, label: value }))}
          value={digits}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <KspResults
          fmt={fmt}
          inputs={{
            formula,
            ksp: kspValue !== null && !bad(kspValue) ? kspValue : null,
            cationInit:
              cationValue !== null && !bad(cationValue) ? cationValue : null,
            anionInit:
              anionValue !== null && !bad(anionValue) ? anionValue : null,
            sig: Number(digits),
            badKsp: bad(kspValue),
            badConc: bad(cationValue) || bad(anionValue),
          }}
          strings={strings}
        />
      </section>
      <KspTable fmt={fmt} sig={Number(digits)} strings={strings} />
    </>
  );
}
